/**
 * Environment Configuration
 * Supports both CI/CD template replacement and local .env files
 *
 * Priority:
 * 1. Local Dev: Always uses import.meta.env from .env.local (if defined)
 * 2. Production: Uses template strings replaced by CI/CD at runtime
 */
/* eslint-disable no-template-curly-in-string */

/**
 * Environment variable resolver
 * @param viteValue - Value from import.meta.env (Vite environment variables from .env.local)
 * @param template - Template string that CI/CD replaces at runtime (e.g., '${ENV_VITE_PKCE}')
 * @returns Resolved environment variable value
 */
const env = (viteValue: string | undefined, template: string): string => {
  // Priority 1: Use .env.local value if defined (even if empty string)
  if (viteValue !== undefined) {
    return viteValue
  }

  // Priority 2: Use template (will be replaced by CI/CD, or remain as template string)
  return template
}

// Configuration values - CI/CD will replace template strings at runtime
export const ISSUER = env(import.meta.env.VITE_OKTA_ISSUER, '${ENV_VITE_OKTA_ISSUER}')

export const CLIENT_ID = env(import.meta.env.VITE_OKTA_CLIENT_ID, '${ENV_VITE_OKTA_CLIENT_ID}')

export const PORT = env(import.meta.env.VITE_PORT, '${ENV_VITE_PORT}')

export const REDIRECT_URI = env(import.meta.env.VITE_OKTA_REDIRECT_URI, '${ENV_VITE_OKTA_REDIRECT_URI}')

export const LOGOUT_URI = env(
  import.meta.env.VITE_OKTA_POST_LOGOUT_REDIRECT_URI,
  '${ENV_VITE_OKTA_POST_LOGOUT_REDIRECT_URI}',
)

export const API_BASE_URL = env(import.meta.env.VITE_API_BASE_URL, '${ENV_VITE_API_BASE_URL}')

export const ASK_DEVLENS_ENDPOINT_MODE = env(
  import.meta.env.VITE_ASK_DEVLENS_ENDPOINT_MODE,
  '${ENV_VITE_ASK_DEVLENS_ENDPOINT_MODE}',
)

export const ASK_DEVLENS_MOCK_BASE_URL = env(
  import.meta.env.VITE_ASK_DEVLENS_MOCK_BASE_URL,
  '${ENV_VITE_ASK_DEVLENS_MOCK_BASE_URL}',
)

export const ASK_DEVLENS_AGENTIC_CHAT_DEBUG_MODE =
  env(import.meta.env.VITE_ASK_DEVLENS_AGENTIC_CHAT_DEBUG_MODE, '${ENV_VITE_ASK_DEVLENS_AGENTIC_CHAT_DEBUG_MODE}') ===
  'true'

export const RAYS_API_BASE_URL = env(import.meta.env.VITE_RAYS_API_BASE_URL, '${ENV_VITE_RAYS_API_BASE_URL}')

export const FULLSTORY_ORG = env(import.meta.env.VITE_FULLSTORY_ORG, '${ENV_VITE_FULLSTORY_ORG}')

export const FULLSTORY_NAMESPACE = env(import.meta.env.VITE_FULLSTORY_NAMESPACE, '${ENV_VITE_FULLSTORY_NAMESPACE}')

export const PKCE = env(import.meta.env.VITE_PKCE, '${ENV_VITE_PKCE}')

export const ENV = env(import.meta.env.VITE_ENV, '${ENV_VITE_ENV}')

export const APPD_KEY = env(import.meta.env.VITE_APP_APPD_KEY, '${ENV_VITE_APP_APPD_KEY}')

export const GROWTHBOOK_API_HOST = env(
  import.meta.env.VITE_APP_GROWTHBOOK_API_HOST,
  '${ENV_VITE_APP_GROWTHBOOK_API_HOST}',
)

export const GROWTHBOOK_CLIENT_KEY = env(
  import.meta.env.VITE_APP_GROWTHBOOK_CLIENT_KEY,
  '${ENV_VITE_APP_GROWTHBOOK_CLIENT_KEY}',
)

// DatoCMS CMS Integration
export const DATOCMS_API_URL = env(import.meta.env.VITE_DATOCMS_API_URL, '${ENV_VITE_DATOCMS_API_URL}')

export const DATOCMS_API_TOKEN = env(import.meta.env.VITE_DATOCMS_API_TOKEN, '${ENV_VITE_DATOCMS_API_TOKEN}')

export const DATOCMS_ENVIRONMENT = env(import.meta.env.VITE_DATOCMS_ENVIRONMENT, '${ENV_VITE_DATOCMS_ENVIRONMENT}')

export const CMS_ENABLED = env(import.meta.env.VITE_CMS_ENABLED, '${ENV_VITE_CMS_ENABLED}')

// DevX App URLs for App Switcher
export const DEVX_HOME_URL = env(import.meta.env.VITE_DEVX_HOME_URL, '${ENV_VITE_DEVX_HOME_URL}')

export const DEVX_INFRA_URL = env(import.meta.env.VITE_DEVX_INFRA_URL, '${ENV_VITE_DEVX_INFRA_URL}')

export const DEVX_DAILY_URL = env(import.meta.env.VITE_DEVX_DAILY_URL, '${ENV_VITE_DEVX_DAILY_URL}')

export const DEVX_AI_URL = env(import.meta.env.VITE_DEVX_AI_URL, '${ENV_VITE_DEVX_AI_URL}')

export const DEVX_LAUNCHPAD_URL = env(import.meta.env.VITE_DEVX_LAUNCHPAD_URL, '${ENV_VITE_DEVX_LAUNCHPAD_URL}')

// ServiceNow Integration
export const SERVICENOW_BASE_URL = env(import.meta.env.VITE_SERVICENOW_BASE_URL, '${ENV_VITE_SERVICENOW_BASE_URL}')

// For backward compatibility with code that imports the default export
const envConfig = {
  ISSUER,
  CLIENT_ID,
  PORT,
  REDIRECT_URI,
  LOGOUT_URI,
  API_BASE_URL,
  ASK_DEVLENS_ENDPOINT_MODE,
  ASK_DEVLENS_MOCK_BASE_URL,
  RAYS_API_BASE_URL,
  FULLSTORY_ORG,
  FULLSTORY_NAMESPACE,
  ENV,
  APPD_KEY,
  GROWTHBOOK_API_HOST,
  GROWTHBOOK_CLIENT_KEY,
  DEVX_HOME_URL,
  DEVX_INFRA_URL,
  DEVX_DAILY_URL,
  DEVX_AI_URL,
  DEVX_LAUNCHPAD_URL,
  DATOCMS_API_URL,
  DATOCMS_API_TOKEN,
  DATOCMS_ENVIRONMENT,
  CMS_ENABLED,
  SERVICENOW_BASE_URL,
}

export default envConfig
